import {
  ContractContentItem,
  ContractInterface,
  MarketmakerStockItem,
  MarketmakerTemplate,
  MarketmakerOfferInterface,
  PersonalOfferForBuyer,
} from '@/types/types'
import {stockCatalogueEntry} from '@/helpers/stockCatalogue'

export const CONTRACT_DESCRIPTION_LANG = 'English'

export const STOCK_DESCRIPTION_FALLBACK_LANG = 'en'

export const STOCK_DESCRIPTION_PREVIEW_LENGTH = 70

export const SALE_CURRENCY = 'reex'

export const TEMPLATE_ALLOWED_COINS = ['dominion', 'hexa']

export const NOT_MARKET_MAKER_CODE = '14000'
export const NO_SUBSCRIPTION_CODE = '14001'
export const NO_MARKET_MAKER_FOUND_CODE = '14005'
export const IS_MARKET_MAKER_CODE = '14006'
export const NO_MOG_FOUND_CODE = '14009'

export const isMarketMakerAccessError = (code?: string | number | null) => {
  const c = String(code ?? '')
  return c === NOT_MARKET_MAKER_CODE || c === NO_SUBSCRIPTION_CODE
}

export const isNotMarketMaker = (code?: string | number | null) =>
  String(code ?? '') === NOT_MARKET_MAKER_CODE

export const isMarketMakerCannotBuy = (code?: string | number | null) =>
  String(code ?? '') === IS_MARKET_MAKER_CODE

export const isNoMarketMakerFound = (code?: string | number | null) =>
  String(code ?? '') === NO_MARKET_MAKER_FOUND_CODE

export const isPersonalShopUnavailable = (code?: string | number | null) =>
  isMarketMakerCannotBuy(code) || isNoMarketMakerFound(code)

export const isEmptyReferralList = (code?: string | number | null) =>
  String(code ?? '') === NO_MOG_FOUND_CODE

export const COIN_LABELS: {[key: string]: string} = {
  hexa: 'Hexa Coin',
  reex: 'Reex Coin',
  octa: 'Octa Coin',
  hepta: 'Hepta Coin',
  terra: 'Terra Coin',
  loyalty: 'Loyalty Coin',
  dominion: 'Dominion Coin',
}

export const isCoin = (sourceType: string) =>
  Object.prototype.hasOwnProperty.call(COIN_LABELS, sourceType)

export const getStockImageUrl = (name?: string | null) =>
  name ? `${process.env.REACT_APP_SHARE_BASE_URL}/${name}` : ''

export const SOURCE_TYPE_LABEL_KEYS: {[sourceType: string]: string} = {
  SOURCE_ORB: '1752',
  R_ORB_TEMPLATE: '1753',
  R_ORB_TEMPLATE_MAP: '1754',
  REEXMINER: '1755',
}

export const UNKNOWN_ITEM_LABEL_KEY = '1756'

export const sourceTypeLabelKey = (sourceType: string) =>
  SOURCE_TYPE_LABEL_KEYS[sourceType] ?? UNKNOWN_ITEM_LABEL_KEY

export const itemTypeLabelKey = (sourceType: string) =>
  isCoin(sourceType) ? '' : (SOURCE_TYPE_LABEL_KEYS[sourceType] ?? '')

const normalizedLabel = (value: string) =>
  value.toLowerCase().replace(/[^a-z0-9]/g, '')

const isGeneratedSuffix = (part: string) => {
  if (part.length < 5 || !/^[A-Za-z0-9]+$/.test(part)) return false
  if (/\d/.test(part)) return true
  return part.slice(1).replace(/[^A-Z]/g, '').length >= 2
}

export const stockItemLabel = (item: MarketmakerStockItem): string => {
  if (isCoin(item.source_type)) return COIN_LABELS[item.source_type]

  const raw = item.img || item.icon_img || ''
  const base = raw.replace(/\.[a-z0-9]+$/i, '')
  const parts = base.split('_').filter(Boolean)
  const middle = parts.filter(
    (p, i) =>
      !(i === 0 && parts.length > 1 && /^\d+$/.test(p)) &&
      !(i === parts.length - 1 && parts.length > 1 && isGeneratedSuffix(p))
  )
  const name = middle.join(' ').trim()

  return normalizedLabel(name) === normalizedLabel(item.source_type) ? '' : name
}

export const contentItemLabel = (item: ContractContentItem): string => {
  if (isCoin(item.source_type)) return COIN_LABELS[item.source_type]
  if (item.name) return item.name

  return stockItemLabel({
    source_type: item.source_type,
    ref_id: item.ref_id,
    counter: item.counter,
    img: item.img ?? '',
    icon_img: item.icon_img ?? '',
  })
}

export const offerImageName = (
  img: string | null | undefined,
  content: unknown
): string => {
  if (img) return img
  if (!Array.isArray(content)) return ''

  const withImage = content.find(
    (item: ContractContentItem) => item.img || item.icon_img
  )
  return withImage?.img || withImage?.icon_img || ''
}

export const stockMapOf = (stock: MarketmakerStockItem[]) => {
  const map = new Map<string, MarketmakerStockItem>()
  stock.forEach((s) => map.set(`${s.source_type}:${s.ref_id}`, s))
  return map
}

export const withStockInfo = (
  content: unknown,
  stockMap: Map<string, MarketmakerStockItem>
): ContractContentItem[] => {
  if (!Array.isArray(content)) return []
  return content.map((item: ContractContentItem) => {
    const match =
      stockMap.get(`${item.source_type}:${item.ref_id}`) ??
      stockCatalogueEntry(item.source_type, item.ref_id)
    if (!match) return item
    return {
      ...item,
      img: item.img ?? match.img,
      icon_img: item.icon_img ?? match.icon_img,
      description: item.description ?? match.description,
    }
  })
}

export const parseStockDescription = (
  raw: unknown,
  language?: string
): string => {
  let data: any = raw

  if (typeof raw === 'string') {
    const trimmed = raw.trim()
    if (!trimmed) return ''
    try {
      data = JSON.parse(trimmed)
    } catch {
      return trimmed
    }
  }

  if (!data || typeof data !== 'object') return ''

  const code = String(language ?? '')
    .slice(0, 2)
    .toLowerCase()
  const value =
    data[code] ??
    data[STOCK_DESCRIPTION_FALLBACK_LANG] ??
    Object.values(data)[0]

  return typeof value === 'string' ? value.trim() : ''
}

export const shortenStockDescription = (text: string): string =>
  text.length > STOCK_DESCRIPTION_PREVIEW_LENGTH
    ? text.slice(0, STOCK_DESCRIPTION_PREVIEW_LENGTH).trimEnd() + '…'
    : text

const parseContent = (raw: unknown): ContractContentItem[] => {
  if (Array.isArray(raw)) return raw as ContractContentItem[]
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw || '[]')
      return Array.isArray(parsed) ? parsed : []
    } catch {
      return []
    }
  }
  return []
}

const parseDescription = (raw: unknown): string => {
  if (typeof raw === 'object' && raw) {
    return (raw as any)?.[CONTRACT_DESCRIPTION_LANG] ?? ''
  }
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw || '{}')
      return parsed?.[CONTRACT_DESCRIPTION_LANG] ?? raw
    } catch {
      return raw
    }
  }
  return ''
}

const parsePersonalOffer = (raw: any): PersonalOfferForBuyer | null => {
  if (!raw || typeof raw !== 'object') return null
  const price = Number(raw.price ?? 0)
  const amount = Number(raw.amount ?? raw.counter ?? 0)
  if (!(price > 0) || !(amount > 0)) return null
  return {price, amount}
}

export const calcOfferTotal = (
  quantity: number,
  price: number,
  personalOffer?: PersonalOfferForBuyer | null
): number => {
  if (!personalOffer) return quantity * price
  const personalQty = Math.min(quantity, personalOffer.amount)
  return personalQty * personalOffer.price + (quantity - personalQty) * price
}

export const normalizeOffers = (data: any): MarketmakerOfferInterface[] => {
  const toOffer = (
    raw: any,
    isMine: boolean,
    id?: string
  ): MarketmakerOfferInterface => ({
    id: String(id ?? raw?.id ?? raw?.template_id ?? ''),
    name: raw?.template_name ?? raw?.name ?? '',
    description: parseDescription(raw?.description),
    price: Number(raw?.price ?? 0),
    img: raw?.img ?? null,
    content: parseContent(raw?.content),
    count: Number(raw?.counter ?? raw?.count ?? raw?.available ?? 0),
    isMine: Boolean(raw?.is_mine ?? raw?.isMine ?? isMine),
    sellerName: raw?.seller_name ?? raw?.owner_name ?? raw?.marketmaker_name,
    personalOffer: parsePersonalOffer(raw?.personal_offer),
  })

  const toList = (src: any, isMine: boolean): MarketmakerOfferInterface[] => {
    if (Array.isArray(src)) return src.map((o) => toOffer(o, isMine))
    if (src && typeof src === 'object')
      return Object.entries(src).map(([id, o]) => toOffer(o, isMine, id))
    return []
  }

  if (!data) return []
  if (Array.isArray(data)) return toList(data, false)
  return [...toList(data.store, false), ...toList(data.my ?? data.mine, true)]
}

export const parseTemplates = (templates: {
  [id: string]: MarketmakerTemplate
}): ContractInterface[] => {
  if (!templates) return []
  return Object.entries(templates)
    .map(([id, tpl]) => {
      let content: ContractContentItem[] = []
      let description = ''
      try {
        content = JSON.parse(tpl.content || '[]')
      } catch {
        content = []
      }
      try {
        const parsed = JSON.parse(tpl.description || '{}')
        description = parsed?.[CONTRACT_DESCRIPTION_LANG] ?? ''
      } catch {
        description = ''
      }
      return {
        id,
        name: tpl.name,
        description,
        price: tpl.price,
        img: tpl.img,
        content,
        locked: tpl.locked,
        counter: tpl.counter ?? 0,
        potential: Math.max(0, Math.floor(Number(tpl.potential) || 0)),
        sortOrder: tpl.sort_order ?? 0,
      }
    })
    .sort((a, b) => a.sortOrder - b.sortOrder || Number(a.id) - Number(b.id))
}
